import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@sphinx/design-system/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@sphinx/design-system/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@sphinx/design-system/command";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sphinx/design-system/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import type { HostListItem } from "@/hooks/useClients";
import { resolveHostLogoByName } from "@/lib/host-logo";
import type { HostThemeMode } from "@/lib/client-styles";

/**
 * Data needed to drive the chat-input client chip. The rows are the fixed
 * 10-target matrix rendered as checkboxes; a checked row is in the compare
 * lineup (this browser's selection, persisted by usePersistedHost, up to
 * `maxSelectedHosts` columns, never empty). The first row of the lineup is
 * the lead column and drives the toolbar. Enabling/disabling a client is the
 * Clients page's job — disabled clients are listed dimmed and not pickable.
 * Host compare and model compare stay mutually exclusive — enforced by the
 * parent's `onMultiHostEnabledChange` / `onMultiModelEnabledChange`.
 */
export interface ClientSelectorData {
  hosts: HostListItem[];
  /** Project the targets belong to (single implicit project in sphinx). */
  projectId: string | null;
  /** Cloud project id for cloud-scoped reads (null when none). */
  cloudProjectId: string | null;
  /** Lead target id — the single active client / first compare column. */
  currentHostId: string | null;
  /** Compare lineup (derived from enabled targets, see PlaygroundMain). */
  selectedHostIds: string[];
  /** Switch the single lead target (not comparing). */
  onHostChange: (hostId: string) => void;
  onSelectedHostIdsChange: (ids: string[]) => void;
  onMultiHostEnabledChange: (enabled: boolean) => void;
  enableMultiHost?: boolean;
  maxSelectedHosts?: number;
}

interface ClientSelectorProps extends ClientSelectorData {
  disabled?: boolean;
  isLoading?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  /** Resolved chat theme (host theme ?? app theme) so logos pick the right variant. */
  themeMode?: HostThemeMode | null;
  /** App-surface theme for portal-rendered modal content. */
  modalThemeMode?: HostThemeMode | null;
}

function compactHostLabel(name: string): string {
  return name || "Client";
}

export function ClientSelector({
  hosts,
  currentHostId,
  selectedHostIds,
  onHostChange,
  onSelectedHostIdsChange,
  onMultiHostEnabledChange,
  enableMultiHost = false,
  maxSelectedHosts = 3,
  disabled,
  isLoading,
  onOpenChange,
  align = "start",
  themeMode,
  modalThemeMode = themeMode,
}: ClientSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const keepPopoverOpenRef = useRef(false);
  const keepPopoverOpenTimeoutRef = useRef<number | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  useEffect(() => {
    onOpenChangeRef.current?.(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (
        typeof window !== "undefined" &&
        keepPopoverOpenTimeoutRef.current !== null
      ) {
        window.clearTimeout(keepPopoverOpenTimeoutRef.current);
      }
    };
  }, []);

  // Same keep-open trick as model-selector.tsx: clicks on a checklist row
  // flip this ref on so the next `onOpenChange(false)` is suppressed.
  const requestPopoverStayOpen = () => {
    keepPopoverOpenRef.current = true;
    setIsOpen(true);
    if (typeof window === "undefined") return;
    if (keepPopoverOpenTimeoutRef.current !== null) {
      window.clearTimeout(keepPopoverOpenTimeoutRef.current);
    }
    keepPopoverOpenTimeoutRef.current = window.setTimeout(() => {
      keepPopoverOpenRef.current = false;
      keepPopoverOpenTimeoutRef.current = null;
    }, 0);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && keepPopoverOpenRef.current) return;
    if (
      typeof window !== "undefined" &&
      keepPopoverOpenTimeoutRef.current !== null
    ) {
      window.clearTimeout(keepPopoverOpenTimeoutRef.current);
      keepPopoverOpenTimeoutRef.current = null;
    }
    keepPopoverOpenRef.current = false;
    setIsOpen(nextOpen);
  };

  const hostsById = useMemo(() => {
    const map = new Map<string, HostListItem>();
    for (const host of hosts) map.set(host.hostId, host);
    return map;
  }, [hosts]);

  // Whether the checklist (checkboxes, multi-select) renders at all — when
  // the parent has withdrawn compare (shared session, environment mode) rows
  // fall back to plain single-select of the lead.
  const checklistMode = enableMultiHost && hosts.length > 1;

  const effectiveSelectedHostIds = useMemo(() => {
    if (checklistMode && selectedHostIds.length > 0) return selectedHostIds;
    return currentHostId ? [currentHostId] : [];
  }, [checklistMode, selectedHostIds, currentHostId]);

  const leadHostId = effectiveSelectedHostIds[0] ?? currentHostId ?? null;
  const leadHost = leadHostId ? (hostsById.get(leadHostId) ?? null) : null;
  const leadHostName = leadHost?.name ?? "Select client";
  const leadHostLogo = leadHost?.name
    ? resolveHostLogoByName(leadHost.name, themeMode)
    : null;

  const isComparing = effectiveSelectedHostIds.length > 1;
  const triggerLabel = isComparing
    ? effectiveSelectedHostIds
        .map((hostId) =>
          compactHostLabel(hostsById.get(hostId)?.name ?? hostId)
        )
        .join(", ")
    : compactHostLabel(leadHostName);

  const handleSingleSelect = (hostId: string) => {
    if (hostId !== leadHostId) onHostChange(hostId);
    setIsOpen(false);
  };

  // Checklist rows toggle membership in the compare lineup (this browser's
  // selection, persisted by usePersistedHost). Enabling/disabling a client
  // is the Clients page's job; a disabled client is listed but not pickable.
  // The lineup is capped at `maxSelectedHosts` columns and never empty.
  const handleToggleLineup = (host: HostListItem) => {
    requestPopoverStayOpen();
    if (!host.enabled) {
      toast.message(`${host.name} is disabled — turn it on under Clients`);
      return;
    }
    const inLineup = effectiveSelectedHostIds.includes(host.hostId);
    if (inLineup) {
      if (effectiveSelectedHostIds.length <= 1) return;
      const lineup = effectiveSelectedHostIds.filter((id) => id !== host.hostId);
      onSelectedHostIdsChange(lineup);
      onMultiHostEnabledChange(lineup.length > 1);
      return;
    }
    if (effectiveSelectedHostIds.length >= maxSelectedHosts) {
      toast.message(`Up to ${maxSelectedHosts} clients compare side by side — uncheck one first`);
      return;
    }
    const lineup = [...effectiveSelectedHostIds, host.hostId];
    onSelectedHostIdsChange(lineup);
    onMultiHostEnabledChange(lineup.length > 1);
  };


  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || isLoading}
              className={cn(
                "h-8 rounded-full px-2 text-xs transition-colors hover:bg-muted/80 @max-2xl/toolbar:max-w-none @max-2xl/toolbar:w-8 @max-2xl/toolbar:px-0",
                isComparing ? "max-w-[280px] gap-1" : "max-w-[170px] gap-1"
              )}
              data-testid="client-selector-trigger"
            >
              {isComparing ? (
                <span className="flex min-w-0 items-center gap-1 overflow-hidden @max-2xl/toolbar:hidden">
                  {effectiveSelectedHostIds.map((hostId) => {
                    const host = hostsById.get(hostId);
                    const name = compactHostLabel(host?.name ?? hostId);
                    const logo = resolveHostLogoByName(name, themeMode);
                    return (
                      <span
                        key={hostId}
                        className="inline-flex h-5 w-[82px] min-w-0 shrink-0 items-center gap-1 rounded-full border border-border/50 px-1.5 text-[10px] font-medium text-muted-foreground"
                      >
                        {logo ? (
                          <img
                            src={logo}
                            alt=""
                            className="size-3 shrink-0 object-contain"
                          />
                        ) : (
                          <span
                            aria-hidden
                            className="size-3 shrink-0 rounded-full bg-muted"
                          />
                        )}
                        <span className="truncate">{name}</span>
                      </span>
                    );
                  })}
                </span>
              ) : (
                <>
                  {leadHostLogo ? (
                    <img
                      src={leadHostLogo}
                      alt=""
                      className="size-4 shrink-0 rounded-[3px] object-contain"
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="size-4 shrink-0 rounded-full bg-muted"
                    />
                  )}
                  <span className="truncate text-[10px] font-medium @max-2xl/toolbar:hidden">
                    {triggerLabel}
                  </span>
                </>
              )}
              {isComparing ? (
                leadHostLogo ? (
                  <img
                    src={leadHostLogo}
                    alt=""
                    className="hidden size-4 shrink-0 rounded-[3px] object-contain @max-2xl/toolbar:block"
                  />
                ) : (
                  <span
                    aria-hidden
                    className="hidden size-4 shrink-0 rounded-full bg-muted @max-2xl/toolbar:block"
                  />
                )
              ) : null}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          {isComparing ? "Clients" : "Client"}
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        align={align}
        className="max-h-[min(520px,calc(100vh-6rem))] w-[260px] overflow-hidden p-0"
        side="top"
        sideOffset={8}
        avoidCollisions={false}
        collisionPadding={8}
      >
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Search clients"
            value={search}
            onValueChange={setSearch}
          />

          <CommandList
            className="overscroll-contain pr-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent"
            style={{ maxHeight: 320, overflowY: "auto" }}
          >
            <CommandEmpty>No matching clients.</CommandEmpty>
            {hosts.map((host) => {
              const inLineup = effectiveSelectedHostIds.includes(host.hostId);
              const isChecked = checklistMode
                ? inLineup
                : host.hostId === leadHostId;
              const logo = resolveHostLogoByName(
                host.name,
                modalThemeMode
              );

              return (
                <CommandItem
                  key={host.hostId}
                  value={`${host.name} ${host.hostId}`}
                  onSelect={() =>
                    checklistMode
                      ? handleToggleLineup(host)
                      : handleSingleSelect(host.hostId)
                  }
                  className={cn(
                    "cursor-pointer rounded-sm px-2 py-1 data-[disabled=true]:cursor-not-allowed",
                    !host.enabled && "opacity-50"
                  )}
                  title={host.enabled ? undefined : "Disabled — turn it on under Clients"}
                  data-testid={`client-row-${host.hostId}`}
                  data-enabled={host.enabled ? "true" : "false"}
                >
                  {logo ? (
                    <img
                      src={logo}
                      alt=""
                      className="size-3.5 shrink-0 object-contain"
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="size-3.5 shrink-0 rounded-full bg-muted"
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {host.name}
                  </span>
                  {checklistMode ? (
                    <div
                      className={cn(
                        "ml-auto flex size-4 shrink-0 items-center justify-center rounded-[5px] border transition-[background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.33,1,0.68,1)]",
                        isChecked
                          ? "border-primary bg-primary shadow-sm"
                          : "border-border/60 bg-transparent hover:border-border"
                      )}
                      aria-hidden
                      data-testid={`client-checkbox-${host.hostId}`}
                      data-checked={isChecked ? "true" : "false"}
                    >
                      {isChecked ? (
                        <Check
                          strokeWidth={3}
                          className="size-2.5 animate-in zoom-in-95 fade-in duration-200 fill-none text-primary-foreground"
                        />
                      ) : null}
                    </div>
                  ) : host.hostId === leadHostId ? (
                    <div className="ml-auto size-1.5 shrink-0 rounded-full bg-primary" />
                  ) : null}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
